import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Card,
    Stack,
    Chip,
    Pagination,
    Select,
    MenuItem,
    FormControl,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import spotterTrainingService from '../../../services/spotterTrainingService';

export default function SpotterTrainingList() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    useEffect(() => { load(); }, []);

    async function load() {
        setLoading(true);
        try {
            const allForms = await spotterTrainingService.getAll();
            // Sort by updatedAt descending (newest first) and filter out submitted
            const sorted = (allForms || [])
                .filter(f => f.status !== 'submitted')
                .sort((a, b) => {
                    const dateA = new Date(a.updatedAt || a.createdAt || 0);
                    const dateB = new Date(b.updatedAt || b.createdAt || 0);
                    return dateB - dateA;
                });
            setItems(sorted);
        } catch (e) {
            console.error('Error loading forms:', e);
        } finally {
            setLoading(false);
        }
    }

    const filtered = useMemo(() => {
        return items;
    }, [items]);

    // Pagination calculations
    const paginatedItems = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return filtered.slice(startIndex, endIndex);
    }, [filtered, currentPage, itemsPerPage]);

    const totalPages = Math.ceil(filtered.length / itemsPerPage);

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    };

    const handleItemsPerPageChange = (event) => {
        setItemsPerPage(event.target.value);
        setCurrentPage(1);
    };

    const handleDelete = async (uuid) => {
        if (!window.confirm('Are you sure you want to delete this form?')) return;
        try {
            await spotterTrainingService.delete(uuid);
            await load();
        } catch (e) {
            console.error('Error deleting form:', e);
        }
    };

    const cardTitle = (rec) => {
        const name = rec.spotterName || '';
        if (name) return name;
        return rec.uuid;
    };

    return (
        <Box sx={{
            height: 'calc(100vh - 60px)',
            backgroundColor: '#f5f5f5',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            {/* Header */}
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={() => navigate('/main')} edge="start">
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h6" fontWeight={600}>Spotter Training Forms</Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate('/training/forms/spotter-training')}
                        sx={{ backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#45a049' } }}
                    >
                        New Form
                    </Button>
                </Box>
            </Box>

            {/* List */}
            <Box sx={{ p: 1.5, flexGrow: 1, overflowY: 'auto' }}>
                {loading ? (
                    <Card sx={{ p: 4 }}>
                        <Typography>Loading...</Typography>
                    </Card>
                ) : filtered.length === 0 ? (
                    <Card sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="h6" color="text.secondary">No forms found</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Click "New Form" to create one
                        </Typography>
                    </Card>
                ) : (
                    <>
                        <Stack spacing={1}>
                            {paginatedItems.map((rec) => (
                                <Card key={rec.uuid} sx={{ p: 1.5 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box sx={{ flex: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                <Typography variant="subtitle1" sx={{ fontSize: '0.95rem' }}>
                                                    {cardTitle(rec)}
                                                </Typography>
                                                <Chip
                                                    label={rec.status === 'submitted' ? 'Submitted' : 'Draft'}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: rec.status === 'submitted' ? '#4caf50' : '#9e9e9e',
                                                        color: 'white',
                                                        height: 20
                                                    }}
                                                />
                                            </Box>
                                            <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                                                {rec.supervisorName && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        <strong>Supervisor:</strong> {rec.supervisorName}
                                                    </Typography>
                                                )}
                                                {rec.date && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        <strong>Date:</strong> {rec.date}
                                                    </Typography>
                                                )}
                                            </Stack>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                                Updated: {new Date(rec.updatedAt || rec.createdAt).toLocaleString()}
                                            </Typography>
                                        </Box>
                                        <Stack direction="row" spacing={0.5}>
                                            <IconButton
                                                size="small"
                                                onClick={() => navigate(`/training/forms/spotter-training?uuid=${encodeURIComponent(rec.uuid)}`)}
                                                color="primary"
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDelete(rec.uuid)}
                                                color="error"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Stack>
                                    </Box>
                                </Card>
                            ))}
                        </Stack>

                        {/* Pagination Controls */}
                        {filtered.length > itemsPerPage && (
                            <Box sx={{
                                mt: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: 1
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, filtered.length)} of {filtered.length}
                                    </Typography>
                                    <FormControl size="small">
                                        <Select
                                            value={itemsPerPage}
                                            onChange={handleItemsPerPageChange}
                                            sx={{ minWidth: 80 }}
                                        >
                                            <MenuItem value={10}>10</MenuItem>
                                            <MenuItem value={25}>25</MenuItem>
                                            <MenuItem value={50}>50</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Pagination
                                    count={totalPages}
                                    page={currentPage}
                                    onChange={handlePageChange}
                                    color="primary"
                                    size="medium"
                                    showFirstButton
                                    showLastButton
                                />
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
}
